import { Injectable, inject, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { BehaviorSubject, Subject } from 'rxjs';
import { filter, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { LabelKey } from '../models/report-labels.model';
import { ReportLabelsService } from '../../features/manage-settings/report-labels/report-labels.service';

@Injectable({
  providedIn: 'root',
})
export class PageTitleService implements OnDestroy {
  private readonly router = inject(Router);
  private readonly titleSubject = new BehaviorSubject<string>('Home');
  readonly title$ = this.titleSubject
    .asObservable()
    .pipe(distinctUntilChanged());
  fidelityCheckLabel: string = LabelKey.FIDELITY_CHECK;

  private readonly routeTitles = new Map([
    ['/dashboard', 'Home'],
    ['/organizations', 'Organizations'],
    ['/activity-dashboard', 'Activity Dashboard'],
    ['/dashboard-activity', 'Dashboard'],
    ['/insights', 'Insights'],
    ['/user-management-listing/manage-organization', 'User Management Listing'],
    ['/strategies', 'Strategies'],
    ['/manage-settings', 'Manage Settings'],
    ['/reports', 'Reports'],
    ['/profile', 'Profile'],
    ['/support', 'Frequently Asked Questions'],
    ['/notifications', 'Notifications'],
    ['/user-management', 'Users'],
    ['/bip-template', 'BIP Template'],
    ['/insight-admin', 'Insights Admin'],
    ['/user-search', 'User Directory'],
    ['/broadcast-config', 'Broadcast Config'],
    ['/idp', 'Identity Provider'],
    //here you can add more routes and their corresponding titles
  ]);

  private readonly destroy$ = new Subject<void>();

  constructor(private reportLabelsService: ReportLabelsService) {
    this.reportLabelsService
      .getLabelValue(LabelKey.FIDELITY_CHECK)
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        this.fidelityCheckLabel = value;
        const currentUrl = this.router.url.split('?')[0];
        if (currentUrl === '/fidelity-check') {
          this.titleSubject.next(value);
        }
      });
    this.initializeRouteListener();
  }

  private initializeRouteListener(): void {
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        )
      )
      .subscribe(event => {
        const baseUrl = event.url.split('?')[0];

        if (baseUrl === '/fidelity-check') {
          this.titleSubject.next(this.fidelityCheckLabel);
        } else {
          const title = this.routeTitles.get(baseUrl) ?? '';
          this.titleSubject.next(title);
        }
      });
  }

  setTitle(title: string): void {
    this.titleSubject.next(title);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
